import { useEffect } from 'react';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import merge from 'lodash.merge';
import mapValues from 'lodash.mapvalues';
import Select from '../../../components/Select/Select';
import TextArea from '../../../components/TextArea/TextArea';
import {
  initialValues as defaultInitialValues,
  otherForm
} from '../../../schemas/forms/other.schema';
import Input from '../../../components/Input/Input';
import { ReactComponent as IconAutoFixHigh } from '../../../public/images/svg/icon_auto_fix_high.svg';
import Button from '../../../components/Button/Button';
import Checkbox from '../../../components/Checkbox/Checkbox';

import { CONTENT_TYPE } from '../../../enums';
import { digitsOnly } from './helper';

const OtherForm = ({ contentType, handleContentType, onSubmit, initialValues }) => {
  const formik = useFormik({
    initialValues: { ...defaultInitialValues, contentType },
    validationSchema: otherForm,
    validateOnChange: false,
    validateOnBlur: true,
    validateOnMount: false,
    onSubmit: (form) => {
      onSubmit(otherForm.cast(form, { stripUnknown: true }));
    }
  });

  // https://github.com/jaredpalmer/formik/issues/3602#issuecomment-1512896214
  // tl;dr; for some reason if we call formik as dependency inside the effect it goes into an infinite loop
  const { setValues, setTouched, validateForm } = formik;

  useEffect(() => {
    if (!initialValues) {
      return;
    }

    // make sure that inline object goes first, not to merge into defaultInitialValues directly as it will mutate it
    const formValues = merge({ contentType }, defaultInitialValues, initialValues);
    const touchFields = (v) => (typeof v === 'object' ? mapValues(v, touchFields) : true);
    const touchedFields = touchFields(formValues);

    setValues(formValues)
      .then(() => setTouched(touchedFields))
      .then(() => validateForm(formValues));
  }, [initialValues, contentType, setValues, setTouched, validateForm]);

  return (
    <div
      className="flex flex-wrap flex-auto justify-center align-center mt-4 gap-8"
      data-testid="other-form"
    >
      <div className="w-[594px] max-h-max min-w-[347px] min-h-[594px] bg-white overflow-hidden">
        <div className="bg-gradient-to-r block h-[2px] from-gradient-blue-purple-400 to-gradient-blue-purple-300" />
        <div className="p-6">
          <Select
            id="content-type"
            label="Content Type"
            value={formik.values.contentType}
            onChange={(value) => {
              formik.setFieldValue('contentType', value);
              handleContentType(value);
            }}
            onBlur={formik.handleBlur}
            options={CONTENT_TYPE.toArray()}
            required
            dataTest="other-form-content-type"
            disabled={!!initialValues}
          />
          <TextArea
            label="Free Form Content Request"
            id="contentTopic"
            value={formik.values.contentTopic}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            required
            dataTest="other-form-content-topic"
            error={formik.touched?.contentTopic && formik.errors?.contentTopic}
          />
          <div className="grid grid-rows-1 grid-cols-2 gap-6 whitespace-nowrap">
            <div className="row-start-1 row-end-2 col-start-1 col-end-2">
              <Input
                label="Word Count"
                value={formik.values.wordCount}
                onChange={({ target: { value } }) => {
                  formik.setFieldValue('wordCount', value.replace(/[^\d]/, ''));
                }}
                onPaste={(evt) => {
                  formik.setFieldValue('wordCount', digitsOnly(evt));
                }}
                onBlur={formik.handleBlur}
                error={formik.touched?.wordCount && formik.errors?.wordCount}
                id="wordCount"
                maxLength={4}
                required
                dataTest="other-form-word-count"
                max="2500"
                min="100"
              />
            </div>
            <div className="row-start-1 row-end-2 col-start-2 col-end-3 flex justify-end">
              <Checkbox
                label="Check with Copyscape"
                value={formik.values.executeCopyscapeSearch}
                onChange={({ target: { checked } }) => {
                  formik.setFieldValue('executeCopyscapeSearch', checked);
                }}
                onBlur={formik.handleBlur}
                type="checkbox"
                error={
                  formik.touched?.executeCopyscapeSearch && formik.errors?.executeCopyscapeSearch
                }
                id="copyscape"
                dataTest="mslp-form-copyscape"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="w-screen overflow-hidden p-4 flex justify-center gap-8">
        <Button
          type="submit"
          name="generate"
          dataTest="other-form-generate"
          className="flex justify-center items-center shadow-sm rounded-sm bg-buttonGradient py-4 px-16 text-white font-bold font-sans text-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          onClick={formik.handleSubmit}
          icon={<IconAutoFixHigh className="mr-2" />}
          label="Generate"
          disabled={formik.isSubmitting}
        />
        <Button
          type="button"
          name="reset"
          dataTest="other-form-reset"
          className="flex justify-center bg-transparent py-4 text-purple-200 font-bold font-sans text-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          onClick={formik.resetForm}
          label="Reset"
        />
      </div>
    </div>
  );
};

OtherForm.propTypes = {
  contentType: PropTypes.string.isRequired,
  handleContentType: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default OtherForm;
